/**
 * The choices a requirement form offers.
 *
 * A sibling of `candidateOptions`, kept apart because the two are read by
 * different screens and grow for different reasons: that one follows what a
 * recruiter asks a candidate, this one follows what a client states in a
 * brief. The one list they share — qualifications — is imported from there
 * rather than copied, because a qualification is the same fact in both places.
 *
 * **Offered, never enforced.** Every column behind these is free text or a
 * free number and stays that way. 119 requirements are already on file with
 * values no list contains, and `withStored` is what keeps them showable and
 * savable — see the note there for the defect that guards against.
 */
import type { ClientLifecycle } from '../types';
export { QUALIFICATIONS, QUALIFICATION_GROUPS, withStored } from './candidateOptions';
export type { Choice } from './candidateOptions';

import type { Choice } from './candidateOptions';

/**
 * Industries, as the job portals list them.
 *
 * The spec says "get data from job portal" — this is that vocabulary, trimmed
 * to what an Indian staffing desk actually sources for and ordered by how
 * often these recruiters see it rather than alphabetically. "Other" is last
 * and real: a closed list would refuse the brief that does not fit, and the
 * free-text box beside it is what catches those.
 */
export const INDUSTRY_TYPES: Choice[] = [
  'Hospital & Health Care',
  'Diagnostics & Pathology Labs',
  'Pharmaceuticals',
  'Medical Devices & Equipment',
  'Banking & Financial Services',
  'Insurance',
  'NBFC & Lending',
  'IT Services & Software',
  'ITES & BPO',
  'Telecom',
  'E-commerce',
  'Retail',
  'FMCG',
  'Manufacturing',
  'Automobile',
  'Construction & Real Estate',
  'Logistics & Supply Chain',
  'Education & Training',
  'Hospitality & Travel',
  'Facility Management & Security',
  'Media & Advertising',
  'Consulting & Professional Services',
  'Government & Public Sector',
  'Other',
].map((name) => ({ value: name, label: name }));

/**
 * Functional areas — the department a role sits in.
 *
 * The same list the spec's "Functional Area" and "Job Function" both point at.
 * One list because they are the same question asked twice, which the model
 * already records: two boxes nobody can tell apart get filled inconsistently
 * and then reported on.
 */
export const FUNCTIONAL_AREAS: Choice[] = [
  'Sales & Business Development',
  'Tele Sales & Tele Calling',
  'Customer Support & Service',
  'Marketing & Communications',
  'Nursing & Patient Care',
  'Doctors & Consultants',
  'Pharmacy',
  'Lab & Diagnostics',
  'Front Office & Reception',
  'Human Resources',
  'Finance & Accounts',
  'Operations',
  'Administration & Facilities',
  'IT & Software Development',
  'Data Entry & Back Office',
  'Logistics & Warehouse',
  'Field Service & Technician',
  'Security',
  'Housekeeping',
  'Teaching & Training',
  'Other',
].map((name) => ({ value: name, label: name }));

/**
 * What the client stated about gender.
 *
 * A dropdown for typing convenience and nothing more. The column stays free
 * text, nothing filters or ranks candidates on it, and that is deliberate:
 * see the long comment on `JobOpening.gender_preference`. "Any" is first and
 * is the honest answer for most briefs; "Not stated" leaves the field empty,
 * which is different again — the client said nothing rather than said anyone.
 */
export const GENDER_PREFERENCES: Choice[] = [
  { value: 'Any', label: 'Any' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

/**
 * Age limits, as a ceiling in years.
 *
 * Stored as the words "Up to 28 years", not as the number 28, and that is the
 * point rather than an oversight. A structured bound invites something to
 * compare a date of birth against it, and a screen that quietly drops
 * somebody for their age is a discrimination exposure that leaves no trace of
 * the decision — where a recruiter reading the line uses judgement and can be
 * asked about it. `JobOpening.age_limit` says the same at more length, and a
 * test fails if the field is ever read by a query.
 */
export const AGE_LIMITS: Choice[] = [
  18, 21, 25, 28, 30, 32, 35, 38, 40, 45, 50, 55, 58, 60,
].map((years) => ({ value: `Up to ${years} years`, label: `Up to ${years} years` }));

/**
 * Headcount, as a list rather than a number box.
 *
 * Small numbers individually because that is what most briefs are, then the
 * round ones: a client asking for 47 people is asking for a bulk drive, and
 * they say "fifty" while it is being scoped. Anything already stored that is
 * not on the list survives through `withStored`.
 */
export const HEADCOUNTS: number[] = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 25, 30, 40, 50, 75, 100,
];

/**
 * How the client interviews. Several may be true at once.
 *
 * The spec's "Telephone / Virtual → Face to Face check box - Some time both
 * will be their", which is a checkbox set rather than a dropdown for exactly
 * that reason. Composed into the one free-text `hiring_process` column the
 * screen already has, the same way the work-time controls compose into
 * `work_time` — so a two-stage process reads "Telephonic, then Face to Face"
 * and nothing had to become an enum to say it.
 */
export const INTERVIEW_ROUND_MODES: string[] = [
  'Telephonic',
  'Virtual',
  'Face to Face',
];

/**
 * The chosen modes and any notes, as one line.
 *
 * Order follows `INTERVIEW_ROUND_MODES` rather than the order somebody ticked
 * them, because that order runs cheapest-first and is how these processes
 * actually go. Notes keep whatever was typed, after the modes: a client's
 * "panel of three on Saturdays" is not a mode and must not be lost to one.
 *
 * Null when nothing is ticked and nothing typed, because an empty string
 * stored in a nullable column is a hiring process of "" rather than none.
 */
export function composeHiringProcess(
  modes: string[],
  notes: string,
): string | null {
  const ordered = INTERVIEW_ROUND_MODES.filter((mode) => modes.includes(mode));
  const parts = [ordered.join(', '), notes.trim()].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Which modes a stored hiring process mentions.
 *
 * So the edit form opens with the boxes already ticked rather than making
 * somebody re-tick what they recorded last week. Substring matching, because
 * this column has 119 rows of prose in it that predate the checkboxes and
 * "one telephonic round then in person" should still tick Telephonic.
 */
export function modesFrom(stored: string | null | undefined): string[] {
  const text = (stored ?? '').toLowerCase();
  return INTERVIEW_ROUND_MODES.filter((mode) => {
    if (mode === 'Face to Face') {
      return text.includes('face to face')
        || text.includes('f2f')
        || text.includes('in person');
    }
    return text.includes(mode.toLowerCase());
  });
}

/**
 * Whatever the stored process says beyond the modes.
 *
 * Splits on the separator `composeHiringProcess` writes, so a round trip
 * through the form keeps the notes and does not re-prepend the modes. A value
 * this form never wrote has no separator, and the whole string is treated as
 * notes — which is the safe way round: it stays visible and editable instead
 * of being parsed away.
 */
export function notesFrom(stored: string | null | undefined): string {
  const value = (stored ?? '').trim();
  if (!value) return '';
  const index = value.indexOf(' · ');
  if (index === -1) {
    // No separator. If the whole line is nothing but modes, there are no
    // notes; otherwise keep every character of it.
    const bare = modesFrom(value).join(', ');
    return bare.toLowerCase() === value.toLowerCase() ? '' : value;
  }
  return value.slice(index + 3).trim();
}

/**
 * Several work locations as one string, and back.
 *
 * The column is a single varchar and stays one: the tracker, the candidate
 * match and two exports all read it as a string, and a child table would be a
 * change to four readers for a list that is displayed far more often than it
 * is queried. Joined with ", " and split on commas, which is what somebody
 * typing this field does by hand anyway.
 *
 * Trimmed and de-duplicated, because "Hyderabad, hyderabad" is one location
 * typed twice.
 */
export function splitLocations(stored: string | null | undefined): string[] {
  const seen = new Set<string>();
  return (stored ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => {
      if (!part) return false;
      const key = part.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function joinLocations(locations: string[]): string | null {
  const cleaned = splitLocations(locations.join(','));
  return cleaned.length ? cleaned.join(', ') : null;
}

/**
 * Title Case For A Job Title, as the spec asks.
 *
 * Its own function rather than `titleCaseName`, which is for people: that one
 * knows about particles and initials, and a job title has neither. What it
 * must not do is lowercase what somebody deliberately capitalised, so a word
 * already carrying an inner capital — "BPO", "iOS", "PhD" — is left exactly
 * as typed. Only the plain lower-case words get their first letter raised.
 */
export function titleCaseJobTitle(value: string): string {
  return value
    .split(/(\s+)/)
    .map((part) => {
      if (/\s/.test(part) || part === '') return part;
      if (/[A-Z]/.test(part.slice(1))) return part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join('');
}

/**
 * What a client agreed to, for the client form's dropdown.
 *
 * Mirrors `ClientAgreementState` in `app/models/enums.py`. The empty option is
 * not here because it is not a state: it is the absence of an answer, which
 * the form offers as "Not recorded" and which keeps the client visible to
 * everybody. Only `none` hides a client from recruiters.
 */
/**
 * What the form offers, which is deliberately not everything the server knows.
 *
 * `none` is absent. It is still a real ClientAgreementState and clients on
 * file hold it — it is the value that hides a client from every recruiter —
 * but it is no longer something to pick from a dropdown, because "discussion
 * going on" covers what people were reaching for it to mean and hiding a
 * client is too consequential to be two clicks away in a list of four.
 *
 * A client already stored as `none` still shows it. See `agreementOptions`
 * below, which injects it rather than letting the select fall silently to the
 * first entry and save something nobody chose — the same rule `options` above
 * applies to a negotiated payment term that is off the offered list.
 */
export const AGREEMENT_STATES: Choice[] = [
  { value: 'signed', label: 'Agreement Signed' },
  { value: 'mail_only', label: 'Mail Confirmation Received' },
  { value: 'in_discussion', label: 'Discussion Going On' },
];

/** Everything the server knows, for a value that is already on the record. */
const AGREEMENT_OFF_LIST: Choice[] = [
  { value: 'none', label: 'Nothing Agreed' },
];

/**
 * The states to render, given what this client is already set to.
 *
 * Never drops a stored value. A select whose value is absent from its options
 * renders as though nothing were chosen, and the next save writes that —
 * which would quietly move a deliberately hidden client back into view.
 */
export function agreementOptions(current: string): Choice[] {
  if (!current || AGREEMENT_STATES.some((a) => a.value === current)) {
    return AGREEMENT_STATES;
  }
  const stored = AGREEMENT_OFF_LIST.find((a) => a.value === current);
  return stored ? [...AGREEMENT_STATES, stored] : AGREEMENT_STATES;
}

/**
 * The two states that mean an agreement is in force.
 *
 * Both are workable and the form says so with one word — "Active" — rather
 * than making a reader work out that signed and mail-confirmed have the same
 * consequence. The underlying distinction is kept because it is real: one
 * rests on paper and the other on a mail thread, and only one of those can be
 * produced if the terms are ever disputed.
 *
 * "Discussion going on" is deliberately not here and not in the list above.
 * It is a *pipeline* state — see ClientLifecycle — because this enum records
 * what the client has committed to, and a discussion is the absence of a
 * commitment rather than a kind of one.
 */
export const AGREEMENT_ACTIVE = ['signed', 'mail_only'];

/**
 * The relationship an agreement implies, given where it stands now.
 *
 * Agreeing terms is what makes a client live, so recording a signed agreement
 * or a mail confirmation moves the relationship to Active. Leaving that to be
 * done by hand is how a client ends up with terms on file and a relationship
 * still reading Pipeline, which is the pair every report then disagrees about.
 *
 * **It only ever moves a client forward, and never off Inactive.** A dormant
 * client with a signed agreement in the drawer is still dormant: the agreement
 * says what was once agreed, not that anybody is working them now. Flipping
 * that to Active would reopen a relationship somebody deliberately closed, and
 * put it back in front of every recruiter.
 *
 * Nothing is inferred in the other direction either. "Nothing agreed" and
 * "not recorded" leave the relationship alone -- an active client whose
 * paperwork lapsed is a question for a person, not a field to be cleared by a
 * dropdown.
 */
export function followAgreement(
  agreementState: string,
  current: ClientLifecycle,
): ClientLifecycle {
  // Never off Inactive. A client nobody is working any more stays that way
  // whatever the paperwork says, and reopening one silently would put it back
  // in front of every recruiter. Ending a relationship stays a person's job,
  // which is the one direction this deliberately cannot move.
  if (current === 'inactive') return current;
  if (AGREEMENT_ACTIVE.includes(agreementState)) return 'active';
  if (agreementState === 'in_discussion') return 'pipeline';
  // "Nothing agreed" and "not recorded" imply nothing. An active client whose
  // paperwork lapsed is a question for a person, not a field cleared by a
  // dropdown.
  return current;
}

/** What each state means for who can see the client, said on the form. */
export const AGREEMENT_HINTS: Record<string, string> = {
  '': 'Nobody has recorded an answer. The client stays visible to everyone.',
  signed: 'A signed agreement is on file.',
  mail_only: 'Confirmed by email. Recruiters work this client as normal.',
  in_discussion:
    'Nothing agreed yet, and recruiters still see the client. '
    + 'The relationship moves to Pipeline.',
  none: 'Recruiters will not see this client until something is agreed.',
};
